import type {
  ComponentProps,
  CSSProperties,
  FormEventHandler,
  PointerEvent,
  ReactNode,
} from "react";

import { cn } from "~/lib/utils";

/**
 * The board language (board.css, dated 2026-09-07): the deck card's interior
 * speaks the shell's own law. A board wears its door's hue (--door-accent,
 * FD-CAN-13), the door's light falls onto the masthead, and a tile's glass
 * catches the hue under the pointer. These are the seats; the sheet is the
 * law. A board composes them — it never re-draws a title, a tile or an empty
 * seat with ad-hoc utility classes.
 */

/** The door's downlight: mounts on the door light's own motion seat, so the
 * Settings breathe knob, the ambient-motion knob and reduced motion still it. */
export function BoardLight() {
  return <span aria-hidden="true" className="ncl-board-light" data-light-motion="breathe" />;
}

/** The scrolling board surface: `<section>` with the column inside. */
export function Board({
  className,
  children,
  width = "wide",
  light = true,
  ...props
}: ComponentProps<"section"> & {
  /** `wide` (880px, tools), `reading` (760px, prose), `broad` (1160px, two-pane) or `full` (masthead only; the body is a stage). */
  readonly width?: "wide" | "reading" | "broad" | "full";
  /** The door's downlight on the masthead; off for boards with their own stage. */
  readonly light?: boolean;
}) {
  return (
    <section {...props} className={cn("ncl-board min-h-0 flex-1 overflow-auto", className)}>
      <div className="ncl-board-column" data-width={width}>
        {light ? <BoardLight /> : null}
        {children}
      </div>
    </section>
  );
}

/** The board's name: its door's glyph at the active rung, the title, one lede. */
export function BoardMasthead({
  glyph,
  title,
  titleId,
  lede,
  actions,
  className,
  ...props
}: Omit<ComponentProps<"header">, "title"> & {
  readonly glyph: ReactNode;
  readonly title: ReactNode;
  /** An id for the title when the board's region is labelled by it. */
  readonly titleId?: string;
  readonly lede?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <header
      {...props}
      className={cn("ncl-board-masthead", className)}
      data-actions={actions ? "true" : undefined}
    >
      <span aria-hidden="true" className="ncl-board-glyph">
        {glyph}
      </span>
      <div className="min-w-0">
        <h1 className="ncl-board-title" id={titleId}>
          {title}
        </h1>
        {lede ? <p className="ncl-board-lede">{lede}</p> : null}
      </div>
      {actions ? <div className="ncl-board-masthead-actions">{actions}</div> : null}
    </header>
  );
}

/** A named group of tiles; the note says what the group's items have in common. */
export function BoardGroup({
  title,
  note,
  children,
  className,
  ...props
}: Omit<ComponentProps<"section">, "title"> & {
  readonly title: ReactNode;
  readonly note?: ReactNode;
}) {
  return (
    <section {...props} className={cn("ncl-board-group", className)}>
      <div className="ncl-board-group-head">
        <h2 className="ncl-board-group-title">{title}</h2>
        {note ? <p className="ncl-board-group-note">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

export type BoardTileShape = "card" | "row" | "list";

/** The grid a group's tiles sit in; `shape` sets the column rhythm. */
export function BoardTiles({
  shape = "card",
  className,
  ...props
}: ComponentProps<"div"> & { readonly shape?: BoardTileShape }) {
  return <div {...props} className={cn("ncl-board-tiles", className)} data-shape={shape} />;
}

function trackPointer(event: PointerEvent<HTMLButtonElement>) {
  const rect = event.currentTarget.getBoundingClientRect();
  event.currentTarget.style.setProperty("--mx", `${event.clientX - rect.left}px`);
  event.currentTarget.style.setProperty("--my", `${event.clientY - rect.top}px`);
}

/** One action: a button whose glass lights in the board's hue under the pointer. */
export function BoardTile({
  glyph,
  title,
  body,
  trail,
  shape = "card",
  className,
  onPointerMove,
  ...props
}: Omit<ComponentProps<"button">, "title"> & {
  readonly glyph: ReactNode;
  readonly title: ReactNode;
  readonly body?: ReactNode;
  /** Trailing data (a count, a hostname) in the mono face — real data only. */
  readonly trail?: ReactNode;
  readonly shape?: BoardTileShape;
}) {
  return (
    <button
      type="button"
      {...props}
      className={cn("ncl-board-tile", className)}
      data-shape={shape}
      onPointerMove={(event) => {
        trackPointer(event);
        onPointerMove?.(event);
      }}
    >
      <span aria-hidden="true" className="ncl-board-tile-glyph">
        {glyph}
      </span>
      <span className="ncl-board-tile-text">
        <span className="ncl-board-tile-title">{title}</span>
        {body ? <span className="ncl-board-tile-body">{body}</span> : null}
      </span>
      {trail ? <span className="ncl-board-tile-trail">{trail}</span> : null}
    </button>
  );
}

/** An empty seat: the glyph in the hue and one sentence that says what to do. */
export function BoardEmpty({
  glyph,
  children,
  className,
  ...props
}: ComponentProps<"p"> & { readonly glyph: ReactNode }) {
  return (
    <p {...props} className={cn("ncl-board-empty", className)}>
      <span aria-hidden="true" className="ncl-board-tile-glyph">
        {glyph}
      </span>
      <span>{children}</span>
    </p>
  );
}

/** A form or read-out on the tile's surface — no hover light, a head row with the glyph. */
export function BoardPanel({
  glyph,
  title,
  children,
  className,
  render,
  ...props
}: Omit<ComponentProps<"div">, "title" | "onSubmit"> & {
  readonly glyph?: ReactNode;
  readonly title?: ReactNode;
  /** Render as a `<form>` (with its own handlers) instead of a `<div>`. */
  readonly render?: "form";
  readonly onSubmit?: FormEventHandler<HTMLFormElement>;
}) {
  const head =
    title !== undefined ? (
      <div className="ncl-board-panel-head">
        {glyph ? (
          <span aria-hidden="true" className="ncl-board-tile-glyph">
            {glyph}
          </span>
        ) : null}
        <h3 className="ncl-board-panel-title">{title}</h3>
      </div>
    ) : null;
  const { onSubmit, ...rest } = props;
  const className_ = cn("ncl-board-panel", className);
  return render === "form" ? (
    <form {...(rest as ComponentProps<"form">)} className={className_} onSubmit={onSubmit}>
      {head}
      {children}
    </form>
  ) : (
    <div {...rest} className={className_}>
      {head}
      {children}
    </div>
  );
}

/** Panels side by side, wrapping to one column when the board is narrow. */
export function BoardPanels({ className, ...props }: ComponentProps<"div">) {
  return <div {...props} className={cn("ncl-board-panels", className)} />;
}

/** Fine print under a control: a hint, a record to add, a price note. */
export function BoardFine({ className, ...props }: ComponentProps<"p">) {
  return <p {...props} className={cn("ncl-board-fine", className)} />;
}

/** Read-only rows (addresses, records): the list tile at rest, no pointer. */
export function BoardRows({ className, ...props }: ComponentProps<"ul">) {
  return <ul {...props} className={cn("ncl-board-rows", className)} />;
}

export function BoardRow({
  glyph,
  trail,
  children,
  className,
  ...props
}: ComponentProps<"li"> & { readonly glyph?: ReactNode; readonly trail?: ReactNode }) {
  return (
    <li {...props} className={cn("ncl-board-row", className)}>
      {glyph ? (
        <span aria-hidden="true" className="ncl-board-tile-glyph">
          {glyph}
        </span>
      ) : (
        <span />
      )}
      <span className="ncl-board-row-main">{children}</span>
      {trail ? <span className="ncl-board-row-trail">{trail}</span> : <span />}
    </li>
  );
}

/** A quiet footnote under the board's content. */
export function BoardNote({ className, ...props }: ComponentProps<"p">) {
  return <p {...props} className={cn("ncl-board-note", className)} />;
}

/** A one-line status ("Loading websites…"), never a spinner for a read. */
export function BoardStatus({ className, ...props }: ComponentProps<"p">) {
  return <p {...props} role="status" className={cn("ncl-board-status", className)} />;
}

export type { CSSProperties as BoardStyle };
